<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties } from '$stores/configStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  const API_BASE_URL = '/api/proxy';
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';

  let compliances = [];
  let resultCount = 0;
  // Sites the study configuration excludes from study-level reports (SC-140).
  let excludedSiteIds = [];
  $: selectedSiteExcluded =
    excludedSiteIds.length > 0 &&
    ($selectedSite?.siteId != null && excludedSiteIds.includes(String($selectedSite.siteId)));
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let selectedType = 'ALL';
  let isExporting = false;
  let inRange = true;

  $: cgmIntervalMinutes = $studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5;
  $: cgmDeviceDurationDays = $studyProperties?.glucoseParameters?.cgmDeviceDurationDays ?? 10;
  $: perDayExpected = Math.round(1440 / Math.max(1, Number(cgmIntervalMinutes)));
  $: wearDenominator = perDayExpected * cgmDeviceDurationDays;
  $: featureDescription = `This page displays CGM compliance data for the selected participant or all participants in the study or selected site.
  You can export the data to Excel for further analysis.
  *Wear*: percentage of total readings over the expected maximum for a full device wear, capped with a minimum denominator of 1.
  For this study: ${perDayExpected} readings/day (${cgmIntervalMinutes}-min interval) × ${cgmDeviceDurationDays}-day device lifetime = ${wearDenominator} expected readings per sensor.`;

  //console.log("Component initializing");

  onMount(async () => {
    //console.log("onMount triggered");
    //console.log("selectedStudy value:", $selectedStudy);

    // Set selectedType based on whether a participant is selected
    // selectedType = $selectedParticipant ? 'ALL' : 'ENRO';

    if ($selectedStudy) {
      //console.log("Calling fetchDeviceCompliance with selectedStudy:", $selectedStudy);
      fetchDeviceCompliance(1);
    } else {
      //console.log("No selectedStudy available");
      isLoading = false;
    }
  });

  // Subscribe to selectedStudy changes
  $: {
    //console.log("selectedStudy changed:", $selectedStudy);
    if ($selectedStudy) {
      //console.log("Fetching data for study:", $selectedStudy._id);
      fetchDeviceCompliance(1);
    }
  }

  async function fetchDeviceCompliance(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/glucoseReadings/studyCompliance?page=${page}&pageSize=${limit}&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedType}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      url += `&inRange=${inRange}`;

      const res = await fetch(url);

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch compliance: ${errorText}`);
      }

      const responseData = await res.json();
      //console.log("Response data:", responseData);

      resultCount = responseData.totalCount || 0;
      excludedSiteIds = responseData.excludedSiteIds || [];
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      compliances = responseData.data;
      //console.log("Processed compliances:", compliances.length);
    } catch (err) {
      console.error('Error fetching compliance:', err);
      compliances = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    //console.log("handleRefresh");
    fetchDeviceCompliance(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchDeviceCompliance(newPage);
    }
  }

  async function fetchAllCompliances() {
    try {
      let url = `${API_BASE_URL}/glucoseReadings/studyCompliance?page=1&pageSize=999999&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedType}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      url += `&inRange=${inRange}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all compliances');
      }

      const result = await res.json();
      return result.data || [];
    } catch (err) {
      console.error('Error fetching all compliances:', err);
      return [];
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      const allCompliances = await fetchAllCompliances();

      // Create formatted date string
      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        reportType: 'CGM_ComplianceDetails'
      });

      const formattedData = allCompliances.map((item) => {
        const formatLocalTime = (utcTime, timezone) => {
          if (!utcTime || !timezone) return '';
          return new Date(utcTime).toLocaleString('en-US', { timeZone: timezone });
        };

        return {
          Protocol: demoMode ? 'DEMO-0001' : item.protocol || '',
          Site: item.siteId || '',
          Subject: item.subjectId || '',
          Status: item.subjectStatus || '',
          Device: item.sensor || '',
          Country: item.country || '',
          Visit: item.visit || '',
          Days: item.daysBetween ? parseFloat(Number(item.daysBetween).toFixed(1)) : 0,
          Readings: parseInt(item.count || 0),
          WearPct: item.wearpercentage ? parseFloat(Number(item.wearpercentage).toFixed(1)) : 0,
          'From (Local)': formatLocalTime(item.minRead, item.timezone),
          'To (Local)': formatLocalTime(item.maxRead, item.timezone),
          Timezone: item.timezone || '',
        };
      });

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

<!-- Add the export spinner component -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="CGM Compliance Report" 
  description={featureDescription}
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{:else if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters  -->
{#if selectedSiteExcluded}
  <div class="mx-4 mt-2 px-3 py-2 rounded border border-amber-400 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm" role="alert">
    Site {$selectedSite.siteId} is excluded from study-level reports by the study
    configuration (test/UAT site), so its measurements are not shown here. The
    participant dashboard still shows this subject's readings.
  </div>
{/if}

<Toolbar title="CGM Compliance Report" itemCountTitle="Sensors" itemCount={resultCount}>
  {#if !$selectedParticipant}
    <div class="flex items-baseline gap-2">
      Status:
      <select
        bind:value={selectedType}
        on:change={() => fetchDeviceCompliance(1)}
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL" selected>All</option>
        <option value="COMPD">Completed</option>
        <option value="CONS">Consented</option>
        <option value="DISC">Discontinued</option>
        <option value="INIT">Initiated</option>
        <option value="SCFA">Screen Failed</option>
        <option value="SCRE">Screened</option>
        <option value="ENRO">Screened/Enrolled</option>
      </select>
    </div>
  {/if}
  <div class="flex items-center mx-2 print:hidden">
    <input
      type="checkbox"
      bind:checked={inRange}
      on:change={() => handleRefresh()}
      class="w-4 h-6 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
    />
    <span class="ms-2">Valid Device Range</span>
  </div>
  <div class="flex items-baseline">
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing study compliance report
    </p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site</th>
          <th class="px-4 py-2 text-left bold">Subject</th>
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="px-4 py-2 text-left bold">Country</th>

          <th class="px-4 py-2 text-left bold">Visit</th>

          <th class="px-4 py-2 text-left bold">Sensor</th>
          <th class="px-4 py-2 text-left bold w-48">Readings From - To (Local)</th>
          <th class="px-4 py-2 text-left bold">Count</th>
          <th class="px-4 py-2 text-left bold w-32">Duration</th>
          <th class="px-4 py-2 text-left bold">Wear</th>
        </tr>
      </thead>
      <tbody>
        {#each compliances as compliance, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{compliance.siteId ?? ''}</td>
            <td class="px-4 py-2">{compliance.subjectId ?? ''}</td>
            <td class="px-4 py-2">{compliance.subjectStatus ?? ''}</td>
            <td class="px-4 py-2">{compliance.country ?? ''}</td>

            <td class="px-4 py-2">{compliance.visit ?? ''}</td>

            <td class="px-4 py-2">{compliance.sensor}</td>
            <td class="px-4 py-2 w-52">
              {compliance.minRead
                ? formatToLocalDateTime(compliance.minRead, compliance.timezone)
                : ''}
              <br />
              {compliance.maxRead
                ? formatToLocalDateTime(compliance.maxRead, compliance.timezone)
                : ''}
            </td>
            <td class="px-4 py-2">{compliance.count}</td>
            <td class="px-4 py-2 w-32">
              {Math.floor(compliance.daysBetween)}d
              {Math.floor((compliance.daysBetween % 1) * 24)}h
              {Math.floor((((compliance.daysBetween % 1) * 24) % 1) * 60)}m
            </td>
            <td class="px-4 py-2">{compliance.wearpercentage}%</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Remove the old loader styles */
  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style>
